import { GET_JOURNEY_SEGMENT } from 'store/queries';

const SCHEDULE_CHANGE = 'SCHEDULE_CHANGE';
const JOURNEY_SEGMENTS_CREATED = 'journey_segments_created';

/**
 * @typedef {('SCHEDULE_CHANGE')} ActionType
 */

/**
 * @typedef {('journey_segments_created'|'journey_segments_updated')} JourneySegmentAction
 */

/**
 * @typedef {string} SegmentId - a Segment ID
 */

/**
 * @typedef {string} JourneySegmentId - a JourneySegment ID
 */

/**
 * @typedef {Object} JourneySegment
 * @param {JourneySegmentId} id
 * @param {SegmentId} segmentId
 */

/**
 * @typedef {Object} HistoryDetailData
 * @param {string} content - the content of the note
 * @param {Object[]} segmentIds
 * @param {JourneySegmentAction} segmentIds[].action - the JourneySegment action
 * @param {JourneySegmentId[]} segmentIds[].uuids - the JourneySegment IDs
 */

/**
 * @typedef {Object} HistoryDetails
 * @param {ActionType} actionType
 * @param {HistoryDetailData} data
 */

/**
 * Get the schedule changes from the history details
 *
 * @param {HistoryDetails[]} details
 * @returns {Map<string, HistoryDetailData>|null} Returns a Map of JourneySegment IDs to schedule change data,
 *  or null if there are no applicable notes
 */
export const getScheduleChanges = (details) => {
  const applicableNotes = details.filter(({ actionType }) => actionType === SCHEDULE_CHANGE);

  if (!applicableNotes.length) {
    return null;
  }

  const sortedNotes = [...applicableNotes].sort((a, b) => a.revisionNumber - b.revisionNumber);

  const scheduleChanges = new Map(
    sortedNotes.flatMap(({ data }) => {
      const { segmentIds } = data;
      return segmentIds
        .filter(({ action }) => action === JOURNEY_SEGMENTS_CREATED)
        .flatMap(({ uuids }) => uuids)
        .map((uuid) => [uuid, data]);
    })
  );

  if (!scheduleChanges.size) {
    return null;
  }

  return scheduleChanges;
};

/**
 * Get the journeySegment specified by the journeySegmentId
 *
 * @param {Object} options
 * @param {string} options.bookingReference
 * @param {ApolloClient} options.client
 * @param {JourneySegmentId} options.journeySegmentId
 * @returns {Promise<JourneySegment|null>} Returns the JourneySegment if it exists, otherwise null
 */
export const getSegmentData = async ({ bookingReference, client, journeySegmentId }) => {
  try {
    const { data, errors } = await client.query({
      query: GET_JOURNEY_SEGMENT,
      variables: { bookingReference, journeySegmentId }
    });

    if (errors && errors.length) {
      throw errors[0];
    }

    const { getJourneySegment } = data;
    return getJourneySegment;
  } catch (error) {
    console.error('useScheduleChangedQuery, getSegmentData error', error);
  }

  return null;
};

/**
 * Get all the journeySegments specified by the journeySegmentIds,
 *  and return them as a Map with the key as the journeySegment's `segmentId`
 *
 * @param {Object} options
 * @param {string} options.bookingReference
 * @param {ApolloClient} options.client
 * @param {JourneySegmentId[]} options.journeySegmentIds
 * @returns {Promise<Map<SegmentId, JourneySegment>>}
 */
export const getSegmentsData = async ({ bookingReference, client, journeySegmentIds }) => {
  const segmentsResults = await Promise.all(
    journeySegmentIds.map((journeySegmentId) => getSegmentData({ bookingReference, client, journeySegmentId }))
  );

  const dataMap = new Map(segmentsResults.filter((data) => data).map((data) => [data.segmentId, data]));

  return dataMap;
};
